import { sumBy } from "lodash"

// Company.allocationReferenceRosLevels entries (product part, sizes part, stores part), in the
// default chain order: every same-store level first, then the same levels across all stores. The
// migration's CHECK holds the same list.
export const REFERENCE_ROS_LEVELS = [
  "variant:all_sizes:same_store",
  "product:same_size:same_store",
  "product:all_sizes:same_store",
  "category:same_size:same_store",
  "category:all_sizes:same_store",
  "variant:all_sizes:all_stores",
  "product:same_size:all_stores",
  "product:all_sizes:all_stores",
  "category:same_size:all_stores",
  "category:all_sizes:all_stores",
] as const

export type ReferenceRosLevel = (typeof REFERENCE_ROS_LEVELS)[number]

/** Upper bound for Company.allocationReferenceRosMinLines, mirrored by its CHECK. */
export const MAX_REFERENCE_ROS_MIN_LINES = 50
/** Upper bound for Company.allocationNoRecentSalesDays, mirrored by its CHECK. */
export const MAX_NO_RECENT_SALES_DAYS = 28

/** What a level groups on: the product part, whether sizes must match, whether the store must. */
export function parseReferenceRosLevel(level: ReferenceRosLevel) {
  const [group, sizes, stores] = level.split(":")
  return {
    group: group as "variant" | "product" | "category",
    sameSize: sizes === "same_size",
    sameStore: stores === "same_store",
  }
}

export const REFERENCE_ROS_LEVEL_LABELS: Record<
  ReferenceRosLevel,
  { title: string; desc: string }
> = {
  "variant:all_sizes:same_store": {
    title: "Variant, all sizes, same store",
    desc: "Other sizes of the same variant, in this store.",
  },
  "product:same_size:same_store": {
    title: "Product, same size, same store",
    desc: "Other variants of the product in this size, in this store.",
  },
  "product:all_sizes:same_store": {
    title: "Product, all sizes, same store",
    desc: "Every variant and size of the product, in this store.",
  },
  "category:same_size:same_store": {
    title: "Category, same size, same store",
    desc: "Products in the category in this size, in this store.",
  },
  "category:all_sizes:same_store": {
    title: "Category, all sizes, same store",
    desc: "Every product in the category, in this store.",
  },
  "variant:all_sizes:all_stores": {
    title: "Variant, all sizes, all stores",
    desc: "The same variant, in every store.",
  },
  "product:same_size:all_stores": {
    title: "Product, same size, all stores",
    desc: "Variants of the product in this size, in every store.",
  },
  "product:all_sizes:all_stores": {
    title: "Product, all sizes, all stores",
    desc: "Every variant and size of the product, in every store.",
  },
  "category:same_size:all_stores": {
    title: "Category, same size, all stores",
    desc: "Products in the category in this size, in every store.",
  },
  "category:all_sizes:all_stores": {
    title: "Category, all sizes, all stores",
    desc: "Every product in the category, in every store.",
  },
}

export type RateOfSalePeriod = "recent" | "mid" | "old"

const PERIODS: RateOfSalePeriod[] = ["recent", "mid", "old"]

// Last day of each period, counted back from the as-of date. A shorter window drops the periods
// that start past its end instead of shrinking them. Mirrors the period CASE in
// shared/sql/calculated/AllocationRateOfSale.sql.
const PERIOD_LAST_DAY: Record<RateOfSalePeriod, number> = { recent: 7, mid: 14, old: Infinity }

export type RateOfSalePeriodSpan = { period: RateOfSalePeriod; firstDay: number; lastDay: number }

/** The periods a window has, with their days (1-based, counted back from the as-of date). */
export function rateOfSalePeriods(windowDays: number): RateOfSalePeriodSpan[] {
  const spans: RateOfSalePeriodSpan[] = []
  let firstDay = 1
  for (const period of PERIODS) {
    if (firstDay > windowDays) break
    const lastDay = Math.min(PERIOD_LAST_DAY[period], windowDays)
    spans.push({ period, firstDay, lastDay })
    firstDay = lastDay + 1
  }
  return spans
}

export function rateOfSalePeriodLabel({ firstDay, lastDay }: RateOfSalePeriodSpan) {
  return `Days ${firstDay} to ${lastDay}`
}

/** Integers summing to 100 in the values' proportions (equal when all are 0), the rounding
 * remainder on the first. */
export function toPercentages(values: number[]): number[] {
  const total = values.reduce((sum, value) => sum + value, 0)
  const rounded = values.map((value) =>
    Math.round(total > 0 ? (value / total) * 100 : 100 / values.length),
  )
  rounded[0]! += 100 - rounded.reduce((sum, value) => sum + value, 0)
  return rounded
}

export type RateOfSaleWeights = {
  weightRecentPct: number
  weightMidPct: number
  weightOldPct: number
}

const WEIGHT_FIELDS: Record<RateOfSalePeriod, keyof RateOfSaleWeights> = {
  recent: "weightRecentPct",
  mid: "weightMidPct",
  old: "weightOldPct",
}

/** Stored weights from one weight per period the window has; the periods it drops get 0. */
export function rateOfSaleWeightsFor(windowDays: number, perPeriod: number[]): RateOfSaleWeights {
  const weights: RateOfSaleWeights = { weightRecentPct: 0, weightMidPct: 0, weightOldPct: 0 }
  for (const [index, { period }] of rateOfSalePeriods(windowDays).entries()) {
    weights[WEIGHT_FIELDS[period]] = perPeriod[index]!
  }
  return weights
}

const DEFAULT_PERIOD_WEIGHTS = [50, 30, 20]

export function defaultRateOfSaleWeights(windowDays: number): RateOfSaleWeights {
  const periods = rateOfSalePeriods(windowDays)
  return rateOfSaleWeightsFor(
    windowDays,
    toPercentages(DEFAULT_PERIOD_WEIGHTS.slice(0, periods.length)),
  )
}

/** The weight of each period the window has, scaled to 100% the way the blend weighs them, so
 * weight saved on a period the window drops doesn't show. */
export function visibleRateOfSaleWeights(weights: RateOfSaleWeights, windowDays: number): number[] {
  return toPercentages(
    rateOfSalePeriods(windowDays).map(({ period }) => weights[WEIGHT_FIELDS[period]]),
  )
}

/** No SKU can reach the minimum, so none would get a rate of sale of its own. */
export function minDaysExceedsWindow(settings: { minDaysWithStock: number; windowDays: number }) {
  return settings.minDaysWithStock > settings.windowDays
}

export type WindowValues<T> = { l7: T; l14: T; window: T }

export type RateOfSaleSnapshot = {
  windowDays: number
  weights: Record<RateOfSalePeriod, number>
  minDaysWithStock: number
  daysWithInventory: number
  eligible: boolean
  buckets: Record<RateOfSalePeriod, { days: number; units: number; ros: number | null }>
}

export type RateOfSaleCalculation = Pick<
  RateOfSaleSnapshot,
  "windowDays" | "weights" | "minDaysWithStock" | "buckets"
>

export type RateOfSaleRow = {
  rateOfSale: number | null
  referenceRateOfSale?: number | null
  calculationSnapshot: RateOfSaleSnapshot | null
}

export type RateOfSaleBasis = "own" | "reference" | "none"

/** The Reference ROS level a line ran on, and how many lines with their own ROS it averaged. */
export type ReferenceRosUsed = { level: ReferenceRosLevel; lines: number }

/** Where a run's ROS came from, and the rate it ran on. referenceRos is null on runs before the engine stored it. */
export type RosBasis =
  | { kind: "own"; ros: number }
  | { kind: "reference"; ros: number; referenceRos: ReferenceRosUsed | null }
  | { kind: "none" }

export const ROS_BASIS_KIND_LABELS = {
  own: "Own ROS",
  reference: "Reference ROS",
  none: "No ROS basis",
} as const satisfies Record<RateOfSaleBasis, string>

/** The level's title; plain Reference ROS when the run didn't store the level or stored one that no longer exists. */
export function referenceLevelLabel(level: ReferenceRosLevel | null): string {
  const labels: Partial<Record<string, { title: string }>> = REFERENCE_ROS_LEVEL_LABELS
  return (level && labels[level]?.title) ?? ROS_BASIS_KIND_LABELS.reference
}

/** What the line shows as its ROS basis: Own ROS, Reference ROS or No ROS basis. */
export function rosBasisLabel(basis: RosBasis): string {
  return ROS_BASIS_KIND_LABELS[basis.kind]
}

/** The level's 1-based position in the company's chain; null when the run didn't store the level or the chain no longer has it. */
export function referenceLevelNumber(
  basis: RosBasis | null | undefined,
  chain: readonly string[],
): number | null {
  if (basis?.kind !== "reference" || basis.referenceRos == null) return null
  const index = chain.indexOf(basis.referenceRos.level)
  return index < 0 ? null : index + 1
}

// The ROS basis filter matches these labels.
export const ROS_BASIS_LABELS = [
  ROS_BASIS_KIND_LABELS.own,
  ROS_BASIS_KIND_LABELS.reference,
  ROS_BASIS_KIND_LABELS.none,
]

type ResolvedRateOfSale = {
  rateOfSale: number
  rateOfSaleBasis: RateOfSaleBasis
  // In-stock days over the window; null when nothing is pre-calculated for the line.
  daysWithInventory: number | null
  rosCalculation: RateOfSaleCalculation | null
}

export type RateOfSaleEntry = {
  rateOfSale: number | null
  referenceRateOfSale: number | null
  daysWithInventory: number | null
  rosCalculation: RateOfSaleCalculation | null
}

export function toRateOfSaleEntry(row: RateOfSaleRow): RateOfSaleEntry {
  const snapshot = row.calculationSnapshot
  return {
    rateOfSale: row.rateOfSale,
    referenceRateOfSale: row.referenceRateOfSale ?? null,
    daysWithInventory: snapshot?.daysWithInventory ?? null,
    rosCalculation: snapshot
      ? {
          windowDays: snapshot.windowDays,
          weights: snapshot.weights,
          minDaysWithStock: snapshot.minDaysWithStock,
          buckets: snapshot.buckets,
        }
      : null,
  }
}

/**
 * The rate a line runs on, read the way the engine reads it: the SKU's own rate, else the
 * Reference ROS, else 0 with basis "none" (no row, or below the minimum days w/ stock).
 */
export function resolveRateOfSaleEntry(entry: RateOfSaleEntry | null): ResolvedRateOfSale {
  const shared = {
    daysWithInventory: entry?.daysWithInventory ?? null,
    rosCalculation: entry?.rosCalculation ?? null,
  }
  if (entry?.rateOfSale != null) {
    return { ...shared, rateOfSale: entry.rateOfSale, rateOfSaleBasis: "own" }
  }
  if (entry?.referenceRateOfSale != null) {
    return { ...shared, rateOfSale: entry.referenceRateOfSale, rateOfSaleBasis: "reference" }
  }
  return { ...shared, rateOfSale: 0, rateOfSaleBasis: "none" }
}

/**
 * Units ÷ days w/ stock over the last 7, 14 and window days, read from the stored periods.
 * A window shorter than 7 or 14 days has no figure for it.
 */
export function ratesPerDayWithStock(
  calculation: RateOfSaleCalculation,
): WindowValues<number | null> {
  const { windowDays } = calculation
  const periods = rateOfSalePeriods(windowDays)
  const over = (lastDay: number) => {
    const end = periods.findIndex((span) => span.lastDay === lastDay)
    if (end < 0) return null
    const buckets = periods.slice(0, end + 1).map((span) => calculation.buckets[span.period])
    const days = sumBy(buckets, "days")
    return days > 0 ? sumBy(buckets, "units") / days : null
  }
  return { l7: over(7), l14: over(14), window: over(windowDays) }
}

export type RateOfSaleBreakdownRow = RateOfSalePeriodSpan & {
  days: number
  units: number
  ros: number | null
  weight: number
}

/** The window's periods with their stored facts and weights, and the weighted rate over them. */
export function rateOfSaleBreakdown(calculation: RateOfSaleCalculation): {
  rows: RateOfSaleBreakdownRow[]
  weighted: number | null
} {
  const rows = rateOfSalePeriods(calculation.windowDays).map((span) => ({
    ...span,
    ...calculation.buckets[span.period],
    weight: calculation.weights[span.period],
  }))
  let weightedSum = 0
  let weightSum = 0
  for (const row of rows) {
    if (row.ros == null) continue
    weightedSum += row.ros * row.weight
    weightSum += row.weight
  }
  return { rows, weighted: weightSum > 0 ? weightedSum / weightSum : null }
}

/** A total over lines is the sum of the rates they run on; a line with no ROS basis adds 0. */
export function sumRateOfSale(rows: { rateOfSale: number }[]): number {
  return rows.reduce((total, row) => total + row.rateOfSale, 0)
}
